import { useObservable, useObserve } from "@legendapp/state/react";
import { useBufferController } from "@/services/buffer-controller";
import { createContext, createRef, useRef } from "react";
import {
  TimelinePlaybackContextState,
  TimelinePlaybackContextValue,
} from "./types";
import { ExtendedVisTimeline } from "@/shared/types";
import { getTimezone } from "@/shared";
import { useVideoPlayback } from ".";
import { useViewSettings } from "@/view-settings";
import {
  DetectionsBufferLoader,
  ReprojectionsBufferLoader,
  TrackSnapshotsBufferLoader,
} from "@/services/buffer-controller/types";
import { batch } from "@legendapp/state";
import { useTicker } from "@/shared/hooks/useTicker";

export const TimelinePlaybackContext =
  createContext<TimelinePlaybackContextValue>(
    {} as TimelinePlaybackContextValue
  );

export const VisTimelineContext =
  createContext<React.MutableRefObject<ExtendedVisTimeline | null>>(
    createRef()
  );

export function TimelinePlaybackProvider({
  children,
}: React.PropsWithChildren) {
  const [tracksBuffer, _, detectionsBuffer, reprojectionsBuffer] =
    useBufferController();
  const videoPlayback = useVideoPlayback();
  const visTimeline = useRef<ExtendedVisTimeline | null>(null);
  const viewSettings = useViewSettings();
  const timelinePlayback = useObservable<TimelinePlaybackContextState>({
    currentTime: 0,
    playbackState: "idle",
    playbackLoadingContext: null,
    isManuallySeeking: false,
    zoomLevel: 0,
    dateRange: {
      start: null,
      end: null,
      timezone: getTimezone(),
    },
    mode: "history",
    historyMeta: {
      start: null,
      end: null,
    },
    liveMeta: {
      offset: 2000,
      start: null,
      end: null,
      isLive: false,
      isBehind: false,
    },
    seekAmount: "1.000",
  });

  const checkIfBufferIsBehind = (
    currentTime: number,
    bufferClient:
      | TrackSnapshotsBufferLoader
      | DetectionsBufferLoader
      | ReprojectionsBufferLoader
  ): boolean => {
    const isVideoStandalone = videoPlayback.videoContext.isStandalone.peek();
    const isVideoEnabled = viewSettings.showVideo.peek();
    const shouldByPass = isVideoEnabled && isVideoStandalone;

    if (shouldByPass) return false;

    const [isBufferAvailable] = bufferClient.timeRanges.isBufferAvailable(
      currentTime,
      true
    );

    return !isBufferAvailable && bufferClient.isLoading();
  };

  const isPlaybackEnded = (currentTime: number) => {
    const end = timelinePlayback.dateRange.end.peek();

    if (!end) return false;

    if (currentTime >= end.getTime()) {
      return true;
    }
  };

  const getNewCurrentTime = (
    currentTime: number,
    elapsed: number,
    videoStartTime: Date
  ) => {
    const isVideoEnabled = viewSettings.showVideo.peek();

    if (isVideoEnabled && currentTime >= videoStartTime.getTime()) {
      return videoPlayback.timeAsUTC();
    } else {
      return currentTime + elapsed;
    }
  };

  const hasPlaybackCatchedUpVideoStartTime = (
    currentTime: number,
    videoStartTime: Date
  ) => {
    const isVideoEnabled = viewSettings.showVideo.peek();
    const isPlaying = timelinePlayback.playbackState.peek() === "playing";

    if (!isVideoEnabled) return false;

    return (
      isPlaying &&
      currentTime >= videoStartTime.getTime() &&
      videoPlayback.dashPlayer.isPaused()
    );
  };

  const onFrameUpdated = (elapsed: number) => {
    const isDetectionsMode = viewSettings.viewMode.peek() === "detections";
    const videoStartTime = videoPlayback.videoContext.startTime.peek();
    const currentTime = timelinePlayback.currentTime.peek();
    const newCurrentTime = getNewCurrentTime(
      currentTime,
      elapsed,
      videoStartTime
    );

    if (isPlaybackEnded(newCurrentTime)) {
      timelinePlayback.playbackState.set("idle");
      return;
    }

    if (
      isDetectionsMode &&
      !detectionsBuffer.isImportMode &&
      checkIfBufferIsBehind(newCurrentTime, reprojectionsBuffer)
    ) {
      batch(() => {
        timelinePlayback.playbackState.set("pending");
        timelinePlayback.playbackLoadingContext.set("reprojections-pending");
      });
      return;
    }

    if (
      !isDetectionsMode &&
      checkIfBufferIsBehind(newCurrentTime, tracksBuffer)
    ) {
      batch(() => {
        timelinePlayback.playbackState.set("pending");
        timelinePlayback.playbackLoadingContext.set("tracks-pending");
      });
      return;
    }

    timelinePlayback.currentTime.set(newCurrentTime);

    // Start playing video when playback catches-up video start time
    if (hasPlaybackCatchedUpVideoStartTime(newCurrentTime, videoStartTime)) {
      videoPlayback.dashPlayer.play();
    }
  };

  const ticker = useTicker({
    onFrameUpdated,
  });

  useObserve(timelinePlayback.playbackState, ({ value }) => {
    const isPlaying = value === "playing";

    if (isPlaying) {
      ticker.run();
    } else {
      ticker.pause();
    }
  });

  return (
    <TimelinePlaybackContext.Provider value={timelinePlayback}>
      <VisTimelineContext.Provider value={visTimeline}>
        {children}
      </VisTimelineContext.Provider>
    </TimelinePlaybackContext.Provider>
  );
}
